import { useFormikEnhanced } from '@superdispatch/forms';
import { useSnackbarStack } from '@superdispatch/ui';
import { Button } from '@superdispatch/ui-lab';
import { APIError } from 'shared/api/APIError';
import { APIResponse } from 'shared/api/CarrierAPIClient';
import { isWebView } from 'shared/constants/AppTypeConstants';
import { useInitialValues } from 'shared/form/AppFormik';
import { toFormikValidationSchema } from 'shared/form/FormSchema';
import { useFlaggedSchema } from 'shared/hooks/useFlaggedSchema';
import { DocumentTitle } from 'shared/layout/DocumentTitle';
import { ProfileStateMotorCarrierPermitForm } from 'shared/modules/carrier-profile/core/ProfileStateMotorCarrierPermitForm';
import { trackCarrierProfileEvent } from 'shared/modules/carrier-profile/data/CarrierProfileAnalytics';
import {
  useCarrierProfileAPI,
  useStateMotorCarrierPermit,
} from 'shared/modules/carrier-profile/data/CarrierProfileAPI';
import {
  StateMotorCarrierPermitDTO,
  stateMotorCarrierPermitSchema,
  stateMotorCarrierPermitYupSchema,
} from 'shared/modules/carrier-profile/data/CarrierProfileDTO';
import {
  useSubmitVerificationDocument,
  VerificationDialogs,
} from './core/VerificationDialogs';
import { ProfilePageLayout } from './ProfilePageLayout';
import { useEffect, useMemo } from 'react';

export function ProfileStateMotorCarrierPermitPage() {
  const { addSnackbar } = useSnackbarStack();
  const { updateStateMotorCarrierPermit } = useCarrierProfileAPI();
  const { data, isLoading, isError } = useStateMotorCarrierPermit();

  const schema = useFlaggedSchema(
    'ctms_zod_schema',
    stateMotorCarrierPermitYupSchema,
    stateMotorCarrierPermitSchema,
  );
  const initialValues = useInitialValues(schema, data);
  const validationSchema = useMemo(
    () => toFormikValidationSchema(schema),
    [schema],
  );

  const formik = useFormikEnhanced<StateMotorCarrierPermitDTO, APIResponse>({
    initialValues,
    validationSchema,
    onSubmit: updateStateMotorCarrierPermit,
    onSubmitSuccess: () => {
      addSnackbar('State Motor Carrier Permit was successfully updated', {
        variant: 'success',
      });

      trackCarrierProfileEvent({
        name: 'Carrier Updated Carrier Info',
        utm_medium: 'Carrier Profile',
        utm_content: 'State Motor Carrier Permit',
      });
    },
    onSubmitFailure: ({ message }: APIError) => {
      addSnackbar(`Failed to update State Motor Carrier Permit: ${message}`, {
        variant: 'error',
      });
    },
  });

  const submitVerificationForm = useSubmitVerificationDocument(formik);

  useEffect(() => {
    if (isError) {
      addSnackbar('Failed to fetch State Motor Carrier Permit', {
        variant: 'error',
      });
    }
  }, [isError, addSnackbar]);

  return (
    <>
      <DocumentTitle title="State Motor Carrier Permit - Profile" />

      <VerificationDialogs formik={formik} />

      <ProfilePageLayout
        formik={formik}
        title="State Motor Carrier Permit"
        isLoading={isLoading}
        actions={
          <Button
            type="button"
            onClick={submitVerificationForm}
            pending={formik.isSubmitting}
            disabled={!formik.dirty}
            fullWidth={isWebView}
          >
            Save
          </Button>
        }
      >
        <ProfileStateMotorCarrierPermitForm />
      </ProfilePageLayout>
    </>
  );
}
